'use client';

// Chat — the global live room plus private DMs and Telegram-style groups.
// Same Supabase backend the mobile app uses: LiveChatMessage (roomId=null →
// global/per-instrument room, kept forever; roomId set → persistent member-only room),
// ChatRoom/ChatRoomMember with RLS via is_room_member, RPCs for rooms.
import { supabase } from '@/lib/supabase/client';
import { isNewAccountContactBlock } from '@/lib/moderation/contactGuard';

export interface LiveChatMessage {
  id: string;
  userId: string;
  username: string;
  userImage: string | null;
  text: string | null;
  imageUrl: string | null;
  symbol: string | null;
  roomId: string | null;
  createdAt: string;
  replyToId: string | null;
  pinnedAt: string | null;
  pinnedBy: string | null;
}

/** One reaction type's count on a message, and whether the viewer reacted with it. */
export interface ReactionSummary {
  reaction: 'like' | 'dislike' | 'fire' | 'hundred' | 'laugh';
  count: number;
  mine: boolean;
}

export interface ChatRoomListItem {
  roomId: string;
  type: 'dm' | 'group';
  name: string | null;
  memberCount: number;
  otherUsername: string | null;
  otherImage: string | null;
  otherUserId: string | null;
  lastText: string | null;
  lastImageUrl: string | null;
  lastUsername: string | null;
  lastAt: string;
  unreadCount: number;
  /** Message requests: 'pending' until the recipient accepts. */
  dmStatus: 'pending' | 'accepted';
  requestedBy: string | null;
}

export interface ChatRoomInfo {
  id: string;
  type: 'dm' | 'group';
  name: string | null;
  dmStatus: 'pending' | 'accepted';
  requestedBy: string | null;
  members: { userId: string; username: string; image: string | null }[];
}

const TABLE = 'LiveChatMessage';
const COLS = 'id, userId, username, userImage, text, imageUrl, symbol, roomId, createdAt, replyToId, pinnedAt, pinnedBy';
export const MAX_MESSAGE_LEN = 500;

/** Last N messages, newest first. roomId=null + symbol → per-instrument room; both null → global. */
export async function fetchRecentMessages(
  roomId: string | null,
  limit = 50,
  symbol?: string | null,
): Promise<LiveChatMessage[]> {
  let q = supabase.from(TABLE).select(COLS);
  q = roomId
    ? q.eq('roomId', roomId)
    : symbol
      ? q.is('roomId', null).eq('symbol', symbol) // per-instrument Live trading room
      : q.is('roomId', null).is('symbol', null); // global room excludes symbol rooms
  const { data, error } = await q.order('createdAt', { ascending: false }).limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as LiveChatMessage[];
}

export async function sendMessage(input: {
  text?: string;
  imageUrl?: string;
  roomId?: string | null;
  symbol?: string | null;
  /** Real id of the message being replied to — never client-supplied text/username. */
  replyToId?: string | null;
}): Promise<LiveChatMessage> {
  const { data: sess } = await supabase.auth.getSession();
  const uid = sess.session?.user?.id;
  if (!uid) throw new Error('Please sign in to join the chat.');

  const text = input.text?.trim() || null;
  const { data, error } = await supabase
    .from(TABLE)
    .insert({
      userId: uid,
      text,
      imageUrl: input.imageUrl ?? null,
      roomId: input.roomId ?? null,
      symbol: input.roomId ? null : (input.symbol ?? null), // symbol rooms are roomId-less
      replyToId: input.replyToId ?? null,
    })
    .select(COLS)
    .single();
  if (error) {
    if (error.message.includes('rate_limited')) throw new Error('Slow down — one message every 2 seconds.');
    if (error.message.includes('blocked')) throw new Error('You can no longer message this trader.');
    if (isNewAccountContactBlock(error.message) && text) {
      // Report the attempt for /admin/reports (best-effort), then show the database's own message.
      void fetch('/api/moderation/blocked', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(sess.session?.access_token ? { Authorization: `Bearer ${sess.session.access_token}` } : {}) },
        body: JSON.stringify({ surface: input.roomId ? 'dm' : 'live_chat', text }),
      }).catch(() => {});
    }
    throw new Error(error.message);
  }
  return data as LiveChatMessage;
}

/** One online participant, from realtime presence (anon-key connection). */
export interface PresenceUser {
  key: string;
  username?: string;
  image?: string | null;
}

interface ChatSubscription {
  onMessage: (m: LiveChatMessage) => void;
  onPresence?: (count: number) => void;
  onPresenceUsers?: (users: PresenceUser[]) => void;
}

// One shared channel per topic with dispatched callbacks. Two components can
// watch the same topic at once (React strict-mode double effects, a chat panel
// next to a page): per-component channels would either reuse the already-
// subscribed instance (fatal ".on() after subscribe()" throw) or tear the
// shared channel down under the other component's feet when one unmounts. The
// registry refcounts listeners and only leaves the channel when the last
// subscriber unsubscribes. Same pattern as the mobile app's liveChat.ts.
const chatChannels = new Map<
  string,
  {
    channel: ReturnType<typeof supabase.channel>;
    listeners: Set<ChatSubscription>;
    metaRef: { current?: { username?: string; image?: string | null } };
  }
>();

/** Injective topic encoding — distinct symbols must never share a presence
 * channel. Tickers are uppercase, so the lowercase `-x` marker can't collide. */
function chatTopic(roomId: string | null, symbol?: string | null): string {
  if (roomId) return `room-${roomId}`;
  if (symbol) {
    const enc = symbol.replace(/[^A-Za-z0-9_-]/g, (c) => `-x${c.charCodeAt(0).toString(16)}`);
    return `live-chat-${enc}`;
  }
  return 'live-chat-global';
}

/** Realtime subscription (+ presence). Rooms and symbol rooms also get a poll fallback in the UI. */
export function subscribeToChat(opts: {
  roomId: string | null;
  /** Per-instrument Live trading room (roomId must be null). */
  symbol?: string | null;
  presenceKey: string;
  /** Shown to others in the online strip of symbol rooms. */
  presenceMeta?: { username?: string; image?: string | null };
  onMessage: (m: LiveChatMessage) => void;
  onPresence?: (count: number) => void;
  onPresenceUsers?: (users: PresenceUser[]) => void;
}): () => void {
  const topic = chatTopic(opts.roomId, opts.symbol);
  const listener: ChatSubscription = {
    onMessage: opts.onMessage,
    onPresence: opts.onPresence,
    onPresenceUsers: opts.onPresenceUsers,
  };

  let entry = chatChannels.get(topic);
  if (!entry) {
    // Defensive: drop any client-cached channel for this topic (e.g. still
    // mid-leave from a previous unmount) — channel() would hand back the
    // already-subscribed instance and .on() throws.
    for (const stale of supabase.getChannels()) {
      if (stale.topic === `realtime:${topic}`) void supabase.removeChannel(stale);
    }
    // Presence meta lives in a ref so it can be updated after subscribe
    // (the profile loads async on web) without tearing the channel down.
    const metaRef: { current?: { username?: string; image?: string | null } } = {
      current: opts.presenceMeta,
    };
    const channel = supabase
      .channel(topic, { config: { presence: { key: opts.presenceKey } } })
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: TABLE,
          // Symbol rooms deliberately subscribe UNFILTERED like the global room:
          // special chars in a `symbol=eq.…` filter value can silently invalidate
          // the subscription, and RLS already scopes what anon receives. The
          // `wanted` check below narrows to this room.
          ...(opts.roomId ? { filter: `roomId=eq.${opts.roomId}` } : {}),
        },
        (payload) => {
          const m = payload.new as LiveChatMessage;
          // roomId/symbol `is null` checks run client-side (no `is null` filter support).
          const wanted = opts.roomId
            ? m.roomId === opts.roomId
            : opts.symbol
              ? m.roomId == null && m.symbol === opts.symbol
              : m.roomId == null && m.symbol == null;
          if (!wanted) return;
          chatChannels.get(topic)?.listeners.forEach((l) => l.onMessage(m));
        },
      )
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState<{ username?: string; image?: string | null }>();
        const count = Object.keys(state).length;
        const users = Object.entries(state).map(([key, metas]) => ({
          key,
          username: metas[0]?.username,
          image: metas[0]?.image ?? null,
        }));
        chatChannels.get(topic)?.listeners.forEach((l) => {
          l.onPresence?.(count);
          l.onPresenceUsers?.(users);
        });
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          void channel.track({ joinedAt: new Date().toISOString(), ...(metaRef.current ?? {}) });
        }
      });
    entry = { channel, listeners: new Set(), metaRef };
    chatChannels.set(topic, entry);
  } else if (opts.presenceMeta) {
    entry.metaRef.current = opts.presenceMeta;
  }
  entry.listeners.add(listener);

  return () => {
    const e = chatChannels.get(topic);
    if (!e) return;
    e.listeners.delete(listener);
    if (e.listeners.size === 0) {
      chatChannels.delete(topic);
      void supabase.removeChannel(e.channel);
    }
  };
}

/**
 * Refresh this client's presence meta on an already-open chat channel — used
 * when the profile resolves after mount, so the online strip picks up the
 * username/avatar without a disruptive channel teardown/rejoin.
 */
export function updateChatPresence(
  roomId: string | null,
  symbol: string | null,
  meta: { username?: string; image?: string | null },
): void {
  const e = chatChannels.get(chatTopic(roomId, symbol));
  if (!e) return;
  e.metaRef.current = meta;
  if (e.channel.state === 'joined') {
    void e.channel.track({ joinedAt: new Date().toISOString(), ...meta });
  }
}

// ── Rooms ──

export async function listRooms(): Promise<ChatRoomListItem[]> {
  const { data, error } = await supabase.rpc('list_rooms');
  if (error) throw new Error(error.message);
  return (data ?? []) as ChatRoomListItem[];
}

export async function getOrCreateDm(otherUserId: string): Promise<string> {
  const { data, error } = await supabase.rpc('get_or_create_dm', { other_user_id: otherUserId });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function createGroup(name: string, memberIds: string[]): Promise<string> {
  const { data, error } = await supabase.rpc('create_group', { group_name: name, member_ids: memberIds });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function fetchRoomInfo(roomId: string): Promise<ChatRoomInfo> {
  const [{ data: room, error: rErr }, { data: members, error: mErr }] = await Promise.all([
    supabase.from('ChatRoom').select('id, type, name, dmStatus, requestedBy').eq('id', roomId).single(),
    supabase.from('ChatRoomMember').select('userId, User(username, image)').eq('roomId', roomId),
  ]);
  if (rErr) throw new Error(rErr.message);
  if (mErr) throw new Error(mErr.message);
  return {
    id: (room as any).id,
    type: (room as any).type,
    name: (room as any).name,
    dmStatus: (room as any).dmStatus ?? 'accepted',
    requestedBy: (room as any).requestedBy ?? null,
    members: ((members ?? []) as any[]).map((m) => ({
      userId: m.userId,
      username: m.User?.username ?? 'trader',
      image: m.User?.image ?? null,
    })),
  };
}

/** Recipient accepts a pending DM request. */
export async function acceptDmRequest(roomId: string): Promise<void> {
  const { error } = await supabase.rpc('accept_dm_request', { room: roomId });
  if (error) throw new Error(error.message);
}

/** Recipient declines a pending DM request — the room disappears. */
export async function declineDmRequest(roomId: string): Promise<void> {
  const { error } = await supabase.rpc('decline_dm_request', { room: roomId });
  if (error) throw new Error(error.message);
}

export async function markRoomRead(roomId: string): Promise<void> {
  await supabase.rpc('mark_room_read', { room: roomId });
}

export async function addGroupMembers(roomId: string, memberIds: string[]): Promise<void> {
  const { error } = await supabase.rpc('add_group_members', { room: roomId, member_ids: memberIds });
  if (error) throw new Error(error.message);
}

export async function leaveRoom(roomId: string): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const uid = sess.session?.user?.id;
  if (!uid) throw new Error('Please sign in.');
  const { error } = await supabase.from('ChatRoomMember').delete().eq('roomId', roomId).eq('userId', uid);
  if (error) throw new Error(error.message);
}

// ── Invite links (groups) ──
// Public live rooms need no token — their invite is just the URL. Groups are
// member-only, so the inviter mints a token (RPC, idempotent per room+creator)
// and whoever opens /chat/join/<token> is added on accept.

export async function createRoomInvite(roomId: string): Promise<string> {
  const { data, error } = await supabase.rpc('create_room_invite', { room: roomId });
  if (error) throw new Error(error.message);
  return data as string;
}

export interface RoomInvitePreview {
  roomId: string;
  name: string | null;
  memberCount: number;
  inviterUsername: string | null;
}

/** Group name + size for the join page (works logged-out). null = invalid/expired. */
export async function previewRoomInvite(token: string): Promise<RoomInvitePreview | null> {
  const { data, error } = await supabase.rpc('preview_room_invite', { p_token: token });
  if (error) throw new Error(error.message);
  const row = (data as any[] | null)?.[0];
  if (!row) return null;
  return {
    roomId: row.roomId,
    name: row.name ?? null,
    memberCount: Number(row.memberCount ?? 0),
    inviterUsername: row.inviterUsername ?? null,
  };
}

/** Join the group behind the token; returns the roomId to open. */
export async function acceptRoomInvite(token: string): Promise<string> {
  const { data, error } = await supabase.rpc('accept_room_invite', { p_token: token });
  if (error) {
    if (error.message.includes('invite_invalid')) throw new Error('invite_invalid');
    if (error.message.includes('too_many_members')) throw new Error('too_many_members');
    if (error.message.includes('not_authenticated')) throw new Error('not_authenticated');
    throw new Error(error.message);
  }
  return data as string;
}

/** Build the share URL for a room. Public rooms carry `?join=1` so the page can
 * route a logged-out visitor through sign-in and straight back into the chat. */
export function chatInviteUrl(target: { roomToken?: string; symbol?: string | null }): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : (process.env.NEXT_PUBLIC_SITE_URL || 'https://showmetrade.com');
  if (target.roomToken) return `${origin}/chat/join/${target.roomToken}`;
  return `${origin}/live?room=${encodeURIComponent(target.symbol || 'general')}&join=1`;
}

// ── Live rooms by instrument (lobby) ──

export interface LiveRoomRow {
  symbol: string | null; // null = general room
  market: string | null;
  lastActivityAt: string | null;
  msgs24h: number;
  users24h: number;
  lastText: string | null;
  lastImageUrl: string | null;
  lastUsername: string | null;
  lastAt: string | null;
  /** A message landed after this viewer's last read time for the room (signed-in only). */
  hasUnread: boolean;
  /** Set when this room belongs to a verified trader (market === 'TRADER') rather than an instrument. */
  ownerId: string | null;
  ownerUsername: string | null;
  ownerDisplayName: string | null;
  ownerImage: string | null;
  ownerVerified: boolean;
}

/** Rooms active in the last 7 days + the general room, with counts and last-message preview. */
export async function listLiveRooms(): Promise<LiveRoomRow[]> {
  const { data, error } = await supabase.rpc('list_live_rooms_v2');
  if (error) throw new Error(error.message);
  return ((data ?? []) as any[]).map((r) => ({
    symbol: r.symbol ?? null,
    market: r.market ?? null,
    lastActivityAt: r.lastActivityAt ?? null,
    msgs24h: Number(r.msgs24h ?? 0),
    users24h: Number(r.users24h ?? 0),
    lastText: r.lastText ?? null,
    lastImageUrl: r.lastImageUrl ?? null,
    lastUsername: r.lastUsername ?? null,
    lastAt: r.lastAt ?? null,
    hasUnread: r.hasUnread === true,
    ownerId: r.ownerId ?? null,
    ownerUsername: r.ownerUsername ?? null,
    ownerDisplayName: r.ownerDisplayName ?? null,
    ownerImage: r.ownerImage ?? null,
    ownerVerified: r.ownerVerified === true,
  }));
}

/**
 * Open (or reclaim) the caller's own trader room — one per verified user, keyed by their
 * username. Idempotent; throws 'not_verified' if the caller has no verification tier.
 */
export async function openTraderRoom(): Promise<string> {
  const { data, error } = await supabase.rpc('open_trader_room');
  if (error) {
    if (error.message.includes('not_verified')) throw new Error('not_verified');
    if (error.message.includes('symbol_taken')) throw new Error('symbol_taken');
    if (error.message.includes('not_authenticated')) throw new Error('not_authenticated');
    throw new Error(error.message);
  }
  return data as string;
}

/** Mark a room read now (symbol null/'' = general). Clears its unread badge in the lobby. */
export async function markLiveRoomRead(symbol: string | null): Promise<void> {
  try { await supabase.rpc('mark_room_read', { p_symbol: symbol || null }); } catch { /* best-effort */ }
}

/** Open (or re-activate) an instrument room so it appears in the lobby. Signed-in only. */
export async function openLiveRoom(symbol: string, market?: string | null): Promise<string> {
  const { data, error } = await supabase.rpc('open_live_room', { p_symbol: symbol, p_market: market ?? null });
  if (error) {
    if (error.message.includes('invalid_symbol')) throw new Error('invalid_symbol');
    if (error.message.includes('rate_limited')) throw new Error('rate_limited');
    if (error.message.includes('not_authenticated')) throw new Error('not_authenticated');
    throw new Error(error.message);
  }
  return data as string;
}

/**
 * Lobby presence: one shared channel where every visitor of /live announces which room they
 * are in ('' = general). The lobby derives "N online" per room from it without opening a
 * realtime channel per room. Returns an updater (call when the visitor switches room) and an
 * unsubscribe.
 */
export function subscribeLobbyPresence(opts: {
  presenceKey: string;
  room: string; // '' = general
  onCounts: (counts: Record<string, number>) => void;
}): { setRoom: (room: string) => void; unsubscribe: () => void } {
  const topic = 'live-lobby';
  for (const stale of supabase.getChannels()) {
    if (stale.topic === `realtime:${topic}`) void supabase.removeChannel(stale);
  }
  let room = opts.room;
  let joined = false;
  const channel = supabase.channel(topic, { config: { presence: { key: opts.presenceKey } } });
  channel
    .on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState<{ room?: string }>();
      const counts: Record<string, number> = {};
      for (const metas of Object.values(state)) {
        const r = metas[0]?.room ?? '';
        counts[r] = (counts[r] ?? 0) + 1;
      }
      opts.onCounts(counts);
    })
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        joined = true;
        void channel.track({ room, at: Date.now() });
      }
    });
  return {
    setRoom: (r: string) => {
      room = r;
      if (joined) void channel.track({ room, at: Date.now() });
    },
    unsubscribe: () => {
      joined = false;
      void supabase.removeChannel(channel);
    },
  };
}

// ── Moderation ──

export async function fetchBlockedIds(): Promise<Set<string>> {
  const { data: sess } = await supabase.auth.getSession();
  const uid = sess.session?.user?.id;
  if (!uid) return new Set();
  const { data, error } = await supabase.from('Block').select('blockedId').eq('blockerId', uid);
  if (error) return new Set();
  return new Set(((data ?? []) as { blockedId: string }[]).map((b) => b.blockedId));
}

export async function blockUser(blockedId: string): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const uid = sess.session?.user?.id;
  if (!uid) throw new Error('Please sign in.');
  const { error } = await supabase.from('Block').insert({ blockerId: uid, blockedId });
  if (error && !error.message.includes('duplicate')) throw new Error(error.message);
}

// ── Reactions (like/dislike + fire/hundred/laugh), reply, pin ──

/** Toggle a reaction on a message. like/dislike are mutually exclusive server-side; the
 * fire/hundred/laugh set is independent per user. Returns the message's updated counts. */
export async function toggleLiveChatReaction(
  messageId: string,
  reaction: ReactionSummary['reaction'],
): Promise<ReactionSummary[]> {
  const { data, error } = await supabase.rpc('toggle_live_chat_reaction', {
    p_message_id: messageId,
    p_reaction: reaction,
  });
  if (error) throw new Error(error.message);
  return ((data ?? []) as any[]).map((r) => ({ reaction: r.reaction, count: Number(r.cnt ?? 0), mine: r.mine === true }));
}

/** Bulk-fetch reaction summaries for a batch of messages (initial load + poll refresh). */
export async function fetchLiveChatReactions(messageIds: string[]): Promise<Record<string, ReactionSummary[]>> {
  if (!messageIds.length) return {};
  const { data, error } = await supabase.rpc('get_live_chat_reactions', { p_message_ids: messageIds });
  if (error) throw new Error(error.message);
  const out: Record<string, ReactionSummary[]> = {};
  for (const r of (data ?? []) as any[]) {
    const id = r.messageId as string;
    (out[id] ??= []).push({ reaction: r.reaction, count: Number(r.cnt ?? 0), mine: r.mine === true });
  }
  return out;
}

/** Can the viewer pin/unpin Live room messages? Verified trader; admins are checked separately
 * via /api/forum/moderate (ADMIN_USER_IDS, server-env only — not visible to this RPC). */
export async function canPinLiveMessages(): Promise<boolean> {
  const { data, error } = await supabase.rpc('can_pin_live_messages');
  if (error) return false;
  return data === true;
}

/** Pin/unpin a Live room message (global or per-instrument). Server checks the permission. */
export async function pinLiveMessage(messageId: string, pin: boolean): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('Please sign in.');
  const res = await fetch('/api/live-chat/pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ messageId, pin }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as { error?: string }));
    throw new Error(body.error || `Could not ${pin ? 'pin' : 'unpin'} the message.`);
  }
}

/** Currently pinned messages for a room (global/per-instrument only — roomId is null). */
export async function fetchPinnedMessages(symbol: string | null): Promise<LiveChatMessage[]> {
  let q = supabase.from(TABLE).select(COLS).is('roomId', null).not('pinnedAt', 'is', null);
  q = symbol ? q.eq('symbol', symbol) : q.is('symbol', null);
  const { data, error } = await q.order('pinnedAt', { ascending: false }).limit(10);
  if (error) throw new Error(error.message);
  return (data ?? []) as LiveChatMessage[];
}

export async function reportMessage(messageId: string, reason: string): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const uid = sess.session?.user?.id;
  if (!uid) throw new Error('Please sign in.');
  const { error } = await supabase
    .from('ChatMessageReport')
    .insert({ messageId, reporterId: uid, reason: reason.slice(0, 200) });
  if (error && !error.message.includes('duplicate')) throw new Error(error.message);
}

/**
 * Resize (max 1080px, JPEG q0.75) in a canvas and upload via the
 * live-chat-image-upload edge function (Bearer user JWT — Storage can't
 * validate ES256 user tokens, the function does the privileged write).
 * Both 'live' and 'room' scope images persist (no TTL pruning).
 */
export async function uploadChatImage(file: File, scope: 'live' | 'room'): Promise<{ url: string }> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1080 / bitmap.width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not process the image.'))), 'image/jpeg', 0.75),
  );

  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('Please sign in again.');

  const res = await fetch(
    `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/live-chat-image-upload`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        apikey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
        'Content-Type': 'image/jpeg',
        'x-chat-scope': scope,
      },
      body: blob,
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as { error?: string }));
    throw new Error(body.error || `Upload failed (${res.status})`);
  }
  return (await res.json()) as { url: string };
}
